import { useCallback, useState } from 'react';

/**
 * BR-DSH-00-16 — "Filter choices must persist for the user for the length of
 * the browser session, and must reset to the delivered defaults on the next
 * sign-in."
 *
 * ── THE DECISION THIS BUILDS, AND ITS PROVENANCE ───────────────────────────
 *
 * The rule carries `[NOT IN PROTOTYPE — decide at build time; this is the
 * proposed default.]`, and PART 14 §14.9 question 2 restates it with the
 * proposed default "Persist for the session, reset on next sign-in" under the
 * section preamble "Every one has a proposed default already written into the
 * sections above, so no build is blocked." Built to that default, exactly as
 * BR-DSH-01-08's view-all link was; the row stays formally open, and if the
 * owner overrules it this file is the whole footprint to remove.
 *
 * ── WHY sessionStorage, AND WHY A SIGN-IN STILL CLEARS IT ──────────────────
 *
 * "The length of the browser session" is sessionStorage's own lifetime — one
 * tab, gone when the tab goes, never synced across tabs (a filter is a view
 * choice, not data; two tabs comparing two windows is a feature). But the
 * rule's second clause names an EVENT the storage does not know about: inside
 * one tab a sign-out and a fresh sign-in share a browser session, and on a
 * shared machine the next person would inherit the last person's choices.
 * So `SignInPage` calls `resetDashboardFilters()` on every SUCCESSFUL
 * sign-in, which is the clause read literally — a failed attempt resets
 * nothing (someone mistyping their password in another tab has changed
 * nobody's dashboards).
 *
 * ── WHAT GOES THROUGH HERE ─────────────────────────────────────────────────
 *
 * Every filter on the three customizable boards (DSH-02 range, DSH-03
 * category + window, DSH-04 currency) — and ONLY filters. Widget layouts are
 * §14.1.7's per-USER server store, a different rule with a different
 * lifetime, and BR-DSH-00-09 keeps the two apart: resetting a layout "must
 * not change the user's filter choices", which is trivially true here
 * because the two never share a store.
 *
 * ── WHY EVERY READ GOES THROUGH AN ACCEPT FUNCTION ─────────────────────────
 *
 * The delivered defaults must stay deliverable: a stored value the screen no
 * longer offers (a renamed range key, a category id from another tenant's
 * session, hand-edited storage) must fall back to the default rather than
 * ride into a query. `accept` is each call site's allow-list, so garbage
 * degrades to the delivered default silently — the same posture as the
 * layout store's `resolveLayout`.
 */

const PREFIX = 'dash.filter.';

function read<T>(key: string, delivered: T, accept: (value: unknown) => value is T): T {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (raw === null) return delivered;
    const parsed: unknown = JSON.parse(raw);
    return accept(parsed) ? parsed : delivered;
  } catch {
    // No storage (privacy mode) or unparseable content: the delivered
    // default, and the dashboard still works — persistence is a courtesy.
    return delivered;
  }
}

/**
 * A `useState` whose initial value comes from the session's stored choice and
 * whose setter records the new choice — BR-DSH-00-16's "persist for the
 * length of the browser session".
 *
 * `key` is namespaced per board and filter (`sales.range`,
 * `inventory.categoryId`, …) so boards cannot collide, and so
 * `resetDashboardFilters` can clear the whole family by prefix without
 * knowing who joined it since.
 */
export function useSessionFilter<T>(
  key: string,
  delivered: T,
  accept: (value: unknown) => value is T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => read(key, delivered, accept));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        sessionStorage.setItem(PREFIX + key, JSON.stringify(next));
      } catch {
        /* storage refused — the choice still applies for this mount */
      }
    },
    [key],
  );
  return [value, set];
}

/**
 * BR-DSH-00-16's second clause — "reset to the delivered defaults on the next
 * sign-in". Called by `SignInPage` on a successful sign-in, and from nowhere
 * else: sign-out keeps the choices (the same person coming straight back is
 * the common case), and an expired session that re-authenticates goes through
 * the sign-in page and lands here anyway.
 */
export function resetDashboardFilters(): void {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key !== null && key.startsWith(PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* no storage means nothing was persisted, so the defaults already hold */
  }
}
